import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Download, Monitor, Smartphone } from 'lucide-react';
import { AppChrome } from '../components/alt/AppChrome';
import { ChatsScreen, SharedScreen, WorkflowsScreen, PulsesScreen, SkillsScreen } from '../components/alt/PlatformPillar';
import { ConnectionsScreen } from '../components/alt/ConnectedAppsPillar';
import { BrainFlowExperiment } from '../components/alt/BrainFlowExperiment';
import { ArchitectureWidget } from '../components/alt/ArchitectureWidget';
import { MOSAIC_CARDS } from '../components/alt/AccelerationMosaic';
import { ConnectedAppsTable } from './Alt';
import { SlackPanel } from '../components/SlackIntegrationGraph';
import { CodeTerminal } from './CompanyBrain';
import { SURFACE_PILLS, TOOL_PILLS, CRM_PILLS } from '../components/alt/SurfacePill';
import { SurfacePills } from '../components/exports/SurfacePills';
import { StillProvider } from '../components/alt/stillContext';
import { SharedAccessPanel } from '../components/exports/SharedAccessPanel';
import { SkillShelf } from '../components/exports/SkillShelf';
import { TimeSavedPanel } from '../components/exports/TimeSavedPanel';
import { GigaInChatGPT } from '../components/exports/GigaInChatGPT';
import { useExportFrames } from '../components/exports/useExportFrames';
import { AgentBuilderCard, ApprovalCard, ConnectionsCard, SharedAgentsCard, TemplateWall } from '../components/aiagents/agentArtefacts';

/**
 * Export surface for the product screens used in the Workspace section of the
 * home page.
 *
 * These are vectorised, not screenshotted: the DOM is walked and re-emitted as
 * SVG primitives, so what lands in Figma is real rectangles, real lines and
 * real text layers you can retype and restyle — not a flat picture.
 */

type Screen = {
  id: string;
  name: string;
  note: string;
  section: string;
  /** Product screens sit in app chrome; diagrams and panels stand alone. */
  chrome?: boolean;
  /** Screen already carries its own inner padding. */
  selfPadded?: boolean;
  active?: string;
  /** Screens that are the whole view — no app nav beside them. */
  noSidebar?: boolean;
  /** Wider canvas for the full-bleed diagrams. */
  wide?: boolean;
  /**
   * Artboard width in px, for frames that were composed narrower than the
   * default 900. A card designed as a third of a twelve-column board stretches
   * badly at full width — its lists go from two lines to one and the visual
   * stops matching what ships on the page.
   */
  maxWidth?: number;
  /**
   * PORTRAIT SUPPORT. Most of these assets end up as an overlay on a vertical
   * video, where a 900px-wide frame has to be shrunk until its labels are
   * unreadable. A frame marked `portrait` can genuinely RE-COMPOSE for a
   * narrow artboard — its rows wrap and its grids collapse — so it is exported
   * at phone width with type still at full size, rather than scaled down.
   *
   * Screens reused from the site are not marked: they carry `md:` breakpoints
   * tied to the viewport, not to their container, so narrowing the artboard
   * would squash them rather than reflow them. Those stay landscape.
   */
  portrait?: boolean;
  /**
   * WHEN TO REACH FOR THIS ONE. `note` says what is in the frame; this says
   * what it is FOR. Written for the person scrolling this page looking for
   * something to drop into a video, a deck or a doc — so it names the job
   * ("showing Giga answering in a channel"), not the contents.
   *
   * Kept to a sentence or two. Anyone who needs more than that is looking at
   * the wrong frame, and the answer is a better frame rather than longer copy.
   */
  use: string;
  render: (portrait: boolean) => React.ReactNode;
};
/** Phone-ish artboard width for portrait mode, before the frame's own padding. */
const PORTRAIT_WIDTH = 400;
/**
 * What each group of frames is for, in one line. The section rule already
 * names the pillar; this says which pitch the frames beneath it serve, so you
 * can skip three sections at a glance rather than reading twelve captions.
 */
const SECTION_BLURBS: Record<string, string> = {
  'Connected Apps': 'For the setup story — what Giga is plugged into, whose account it uses, and how that compares to a chatbot with no access.',
  'Company Brain': 'For the "where does it learn this?" question — company knowledge arriving from real systems rather than being typed in.',
  'AI Co Worker': 'For showing Giga working inside a tool your audience already uses, rather than in an app they would have to adopt first.',
  Workspace: 'The product itself. Reach for these when you need a real Giga screen on camera — a chat, a workflow, a schedule, a library.',
  Architecture: 'For technical and buyer conversations — where Giga sits relative to your tools, your data and the models.',
  'Speed & control cards': 'The outcome claims from the home page, as UI. Reach for these when the point is what a team gets back, not how the product works.',
  'AI Agents': 'The pieces of building and running an agent, each on its own. Best used in sequence to walk through creating one.'
};
/**
 * The column width each mosaic card occupies on the home page's twelve-column
 * board, so the exported artboard matches what ships rather than a stretched
 * version of it.
 */
const MOSAIC_WIDTHS: Record<string, number> = {
  'mosaic-offload': 740,
  'mosaic-meetings': 520,
  'mosaic-answers': 420,
  'mosaic-own-data': 420,
  'mosaic-activity': 420,
  'mosaic-teams': 520,
  'mosaic-model': 740
};
/**
 * EXPORT-ONLY REPLACEMENTS FOR MARKETING CARDS.
 *
 * Two of the home page's seven board cards are arguments, not screens: a
 * headline, a paragraph, and a picture underneath. That is right on the page
 * and wrong here — a marketing claim dropped into a product video reads as an
 * ad, and the asset a teammate actually wants is the UI on its own.
 *
 * So these two frames ship purpose-built product panels instead, on the shared
 * export type scale. The other five carry real UI already and are exported
 * exactly as they appear on the home page.
 */
const MOSAIC_REPLACEMENTS: Record<string, {
  name: string;
  note: string;
  use: string;
  render: () => React.ReactNode;
}> = {
  'mosaic-meetings': {
    name: 'Time returned',
    note: 'Meetings replaced, hours back — no headline',
    use: 'Use this when the point is hours given back. It carries the number on screen, so it works as the payoff shot at the end of a demo or over a customer quote.',
    render: () => <TimeSavedPanel data-id="element-9621" data-mp="5r9kfqr1r2je" />
  },
  'mosaic-teams': {
    name: 'Built for teams',
    note: 'Group, access levels and shared connections',
    use: 'Use this for anything about teams rather than individuals — onboarding a department, or answering "who can see this?" The connections sit under the group, which is the point.',
    render: () => <SharedAccessPanel data-id="element-9622" data-mp="3e7l29gmwdif8" />
  }
};
/** The export-only panels reflow properly, so they carry portrait variants. */
const PORTRAIT_READY = new Set(['mosaic-meetings', 'mosaic-teams', 'skills-shelf', 'agent-connections', 'agent-approval', 'agent-builder']);
const SCREENS: Screen[] = [
// ── Connected Apps ───────────────────────────────────────────────────────
{
  id: 'connections',
  name: 'Connections',
  note: 'Tools, accounts and the AI surfaces that reach them',
  use: 'The widest single view of what Giga is plugged into. Reach for this when someone asks "what does it connect to?" — it answers with named tools rather than a logo wall.',
  section: 'Connected Apps',
  chrome: true,
  selfPadded: true,
  noSidebar: true,
  wide: true,
  render: () => <ConnectionsScreen data-id="element-9623" data-mp="3s99vbf0yt9d0" />
}, {
  id: 'comparison',
  name: 'Connection comparison',
  note: 'Giga vs ChatGPT vs Claude',
  use: 'The competitive slide. Use it when the audience already pays for ChatGPT or Claude and needs to see what those cannot reach.',
  section: 'Connected Apps',
  render: () => <ConnectedAppsTable data-id="element-9624" data-mp="5h9w5yr3kyg" />
},
// ── Company Brain ────────────────────────────────────────────────────────
{
  id: 'brain-flow',
  name: 'Sources into tracks',
  note: 'Observations flowing from scans and chats',
  use: 'Use this to explain where the knowledge comes from — real systems being read, sorted into tracks. Good as a background plate for a voiceover, since it reads at a glance without being studied.',
  section: 'Company Brain',
  wide: true,
  render: () => <BrainFlowExperiment settled data-id="element-9625" data-mp="1letn8fpg380b" />
},
// ── AI Co Worker ─────────────────────────────────────────────────────────
{
  id: 'slack',
  name: 'Slack thread',
  note: 'Giga answering in #sales',
  use: 'Reach for this whenever you need to show a conversation happening in Slack — someone asking in a channel and Giga answering there, with no new app to open. The most reusable frame on this page.',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="slack" showPlatformToggle={false} settled data-id="element-9626" data-mp="53pp545kir6s" />
}, {
  // Teams is its own frame rather than a toggle inside the Slack one, so it
  // can be exported and reviewed on its own terms.
  id: 'teams',
  name: 'Teams chat',
  note: 'Giga answering in Microsoft Teams',
  use: 'The same exchange for a Microsoft shop. Swap this in for the Slack frame when the audience runs Teams — showing the wrong chat tool loses the room faster than any other detail.',
  section: 'AI Co Worker',
  render: () => <SlackPanel className="w-full" heightClass="h-[560px]" platform="teams" showPlatformToggle={false} settled data-id="element-9627" data-mp="3kq7dflqwnteh" />
}, {
  id: 'claude-code',
  name: 'Claude Code terminal',
  note: 'Giga over MCP',
  use: 'For developer audiences. Use it on engineering pages, in docs, or anywhere the point is that Giga is reachable over MCP from a terminal rather than only through a UI.',
  section: 'AI Co Worker',
  render: () => <CodeTerminal data-id="element-9628" data-mp="2wyk60n98zb2l" />
}, {
  // The full ChatGPT application rebuild lives on the home page. As an asset
  // it was unreadable at the size anyone views one: sidebar and composer
  // eating the frame, four turns of transcript, and the one thing that matters
  // — Giga reaching a real system — buried inside it. This is that single
  // exchange at a size you can read.
  id: 'chatgpt',
  name: 'Giga inside ChatGPT',
  note: 'One question, the Giga call, the sourced answer',
  use: 'Use this for the "keep using ChatGPT" pitch. It is deliberately one exchange, so it stays readable as a phone overlay or a single slide where a full transcript would not.',
  section: 'AI Co Worker',
  maxWidth: 620 + 80,
  portrait: true,
  render: (portrait: boolean) => <GigaInChatGPT portrait={portrait} data-id="element-9629" data-mp="34ahviwnz4gla" />
},
// ── Workspace ────────────────────────────────────────────────────────────
{
  id: 'chats',
  // "AI chats" is what the nav calls it inside the app, but as a standalone
  // asset it needs to say whose chat it is — an unlabelled chat screen in a
  // video is indistinguishable from any other assistant.
  name: 'Giga Chat',
  note: 'Renewal risk in Salesforce',
  use: 'The default "here is the product" frame. Use it when you need Giga answering in its own app, with the sources it read named underneath the answer.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  selfPadded: true,
  render: () => <ChatsScreen data-id="element-9630" data-mp="s09cd0m4o8y8" />
}, {
  id: 'groups',
  name: 'Groups & permissions',
  note: 'Every group, and the way into each',
  use: 'Use this for the access question — every group in the workspace, who is in it, and whether you get in by invite, by request, or freely. Nothing is cropped or faded here on purpose: a partial view of access answers nobody. Pairs with "Built for teams" below, which zooms into one group.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SharedScreen data-id="element-9631" data-mp="1ax11vrs9jkaq" />
}, {
  id: 'workflows',
  name: 'Workflows',
  note: 'Monday morning update',
  use: 'Use this when the story is multi-step work running on its own — the steps, what each one touches, and the record beside the name: owner, who can run it, last run, and Run now sitting with the title rather than buried under the steps.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <WorkflowsScreen data-id="element-9632" data-mp="2dbep0woe5xrq" />
}, {
  id: 'pulses',
  name: 'Pulses',
  note: 'One running, three scheduled',
  use: 'Use this for the always-on angle — work on a schedule that arrives without being asked. The third row is mid-run, with its step bar showing where it has got to, and every row carries how many times it has fired and since when. That record is the argument: this has been quietly doing the job for months.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <PulsesScreen data-id="element-9633" data-mp="1plkckg7zib80" />
}, {
  id: 'skills',
  name: 'Skills',
  note: 'Written by one person, run by a group',
  use: 'The real Skills view, and the frame for the handover: each row names who wrote the method, the group it was shared into, the faces of everyone who can now run it, and how many times it has been run. Use it when the point is that one person\u2019s expertise becomes the company\u2019s. For a camera-facing version, take the Skills library shelf below.',
  section: 'Workspace',
  chrome: true,
  noSidebar: true,
  render: () => <SkillsScreen data-id="element-9634" data-mp="15ierounpj45d" />
}, {
  // The same idea given the template wall's treatment. The screen above is the
  // real view and stays — but four rows in app chrome says "there are four
  // skills", where the claim is that a company builds a LIBRARY every agent
  // draws on. An oversized shelf dissolving into the ground says that instead,
  // so this is the frame to reach for in a video or a deck.
  id: 'skills-shelf',
  name: 'Skills library',
  note: 'A shelf of methods, one lit — for decks and video',
  use: 'The same idea composed for camera rather than for use. Reach for this in a video or on a slide where the point is the depth of the library; use the Skills screen above when you need the literal UI.',
  section: 'Workspace',
  maxWidth: 900 + 80,
  portrait: true,
  // Four across on a wide artboard, two on a phone — the cards keep their own
  // size either way, which is the point of re-composing rather than scaling.
  render: (portrait: boolean) => <SkillShelf columns={portrait ? 1 : 4} data-id="element-9635" data-mp="20sjlgzycavrn" />
},
// ── Architecture ─────────────────────────────────────────────────────────
{
  id: 'architecture',
  name: 'Architecture',
  note: 'Ways to use Giga',
  use: 'For technical buyers and security reviews. Use it to show where Giga sits between your tools, your data and the models — the frame to bring to an IT conversation.',
  section: 'Architecture',
  wide: true,
  render: () => <ArchitectureWidget data-id="element-9636" data-mp="2khaj8x8fqsbq" />
},
// ── Speed & control board ────────────────────────────────────────────────
// The seven panels from "The fastest version of your company is already
// possible", each on its own artboard. They are pulled from the section's own
// exported registry rather than rebuilt here, so a change to a panel on the
// home page lands in the export automatically. Narrow artboards: these are
// cards, not full screens, and exporting them at 900px would stretch a
// four-column layout that was composed at roughly half that.
...MOSAIC_CARDS.map(card => {
  const swap = MOSAIC_REPLACEMENTS[card.id];
  return {
    id: card.id,
    name: swap?.name ?? card.name,
    note: swap?.note ?? card.note,
    // The five untouched cards keep marketing copy inside the frame, so their
    // guidance says so plainly rather than pretending they are product shots.
    use: swap?.use ?? `The card as it ships on the home page, headline and all. Use it when you want the claim itself on screen — for a product screenshot without marketing copy, reach for a Workspace frame instead.`,
    section: 'Speed & control cards',
    // Each artboard is the card's own column width on the board plus the
    // frame's 40px of padding on each side. The replacements are panels rather
    // than board cards, so they get a little more room for their rows.
    maxWidth: (swap ? 620 : MOSAIC_WIDTHS[card.id] ?? 560) + 80,
    portrait: PORTRAIT_READY.has(card.id),
    render: swap?.render ?? card.render
  };
}),
// ── AI Agents ────────────────────────────────────────────────────────────
// The five artefacts from the AI Agents page, exported BARE — without the
// mosaic panel, tint and copy that frame them on the page. Those are page
// composition; what a designer needs from an export is the product object on
// its own, at the size it was drawn rather than the reduced size it appears at
// inside a marketing card.
{
  id: 'agent-builder',
  name: 'Agent builder',
  note: 'Brief, model, schedule and connections',
  use: 'Step one of the agent story: someone describing the job in plain words. Use it for "how do you make one?" — it is already phone-width, so it drops straight onto a vertical video.',
  section: 'AI Agents',
  // Its own width plus the settings panel it overlaps on the right.
  maxWidth: 340 + 80,
  // Already phone-width by design, so portrait mode leaves it alone.
  portrait: true,
  render: () => <AgentBuilderCard data-id="element-9637" data-mp="1ghe9k8mnp2dd" />
}, {
  id: 'agent-templates',
  name: 'Template shelf',
  note: 'Four pre-built agents, one lit',
  use: 'For the "you do not start from nothing" beat. Use it early in a demo, before the builder, to show there is a shelf to pick from.',
  section: 'AI Agents',
  // The wide canvas the shelf is composed on — on the page it is reduced as a
  // whole object, but the export wants it at full size.
  maxWidth: 900 + 80,
  render: () => <TemplateWall data-id="element-9638" data-mp="3i4teyp3i2ris" />
}, {
  id: 'agent-connections',
  name: 'Connections panel',
  note: 'Two connected, three waiting',
  use: 'A small frame for the moment an agent is given access to a tool. Use it as an inset or overlay rather than a full slide — it is deliberately compact.',
  section: 'AI Agents',
  maxWidth: 230 + 80,
  portrait: true,
  render: () => <ConnectionsCard data-id="element-9639" data-mp="3vxsqyh42q3ll" />
}, {
  id: 'agent-approval',
  name: 'Approval prompt',
  note: 'Decline, always allow, approve',
  use: 'The frame for every trust and safety conversation — an agent stopping to ask before it acts. Reach for this whenever someone worries about AI doing something unattended.',
  section: 'AI Agents',
  maxWidth: 300 + 80,
  portrait: true,
  render: () => <ApprovalCard data-id="element-9640" data-mp="3br43ghd20m98" />
}, {
  id: 'agent-shared',
  name: 'Shared agents',
  note: 'Shared with you · 12 of 24',
  use: 'For the spread of adoption — one person builds an agent, the team inherits it. Use it as the closing frame of the agent sequence.',
  section: 'AI Agents',
  maxWidth: 400 + 80,
  render: () => <SharedAgentsCard data-id="element-9641" data-mp="2y0h5irf4b0e7" />
}];
export function ExportDashboards() {
  const {
    register,
    busy,
    copied,
    warm,
    copy,
    save,
    savePng,
    saveAllPng
  } = useExportFrames();
  /**
   * ORIENTATION IS PER FRAME, NOT PER PAGE.
   *
   * A single page-wide switch was the wrong model: you almost never want every
   * frame in one orientation, you want THIS screen vertical for a short and
   * that diagram wide for a slide. So each frame owns its own state, and the
   * pair of buttons in the header is a "set them all" shortcut rather than the
   * source of truth.
   *
   * `undefined` for a frame means "follow the page default".
   */
  const [pageDefault, setPageDefault] = useState(false);
  const [orientation, setOrientation] = useState<Record<string, boolean>>({});
  const isPortrait = (s: Screen) => !!s.portrait && (orientation[s.id] ?? pageDefault);
  const setAll = (portrait: boolean) => {
    setPageDefault(portrait);
    // Clear the per-frame overrides, otherwise "set all" would visibly skip
    // the frames someone had already switched by hand.
    setOrientation({});
  };
  return <main className="min-h-screen w-full bg-[#F4F1EA] pb-24" data-id="element-9642" data-mp="2nty1ljk1q08b">
      <header className="border-b border-[#E7E0D4] bg-[#FAF8F4]" data-id="element-9643" data-mp="28y8f7kay7l32">
        <div className="mx-auto max-w-[1240px] px-6 py-10" data-id="element-9644" data-mp="2dxl7wloi3mdk">
          <p className="text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9645" data-mp="2e07dwgvqttto">
            Export
          </p>
          <h1 className="mt-3 font-title text-3xl font-semibold tracking-tight text-[#2B211A] md:text-[40px]" data-id="element-9646" data-mp="12ipn0hz9gook">
            Product screen library
          </h1>
          <p className="mt-3 max-w-[64ch] text-[15.5px] leading-relaxed text-[#6B5D52]" data-id="element-9647" data-mp="2f0v2kqgn98z">
            Every product screen, diagram and agent artefact used across the
            site, ready to take. Download a PNG for a video, a deck or a doc, or
            copy one into Figma as editable layers — real rectangles and real
            text you can retype, not a screenshot. Every frame is held on its
            finished state, so nothing arrives mid-animation or half-loaded.
          </p>

          <ol className="mt-6 max-w-[64ch] space-y-2 rounded-xl border border-[#E7E0D4] bg-white px-5 py-4 text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-9648" data-mp="3kl5hgro1d3zk">
            <li data-id="element-9649" data-mp="3atjbpaugvx47">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9650" data-mp="3av2q0vwkby3c">1.</strong> Keep
              this window at desktop width — the app sidebar only renders above
              1024px, and the export captures what is actually on screen.
            </li>
            <li data-id="element-9651" data-mp="31v7a6ds2bewz">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9652" data-mp="2i7b42g7em8li">2.</strong> Hit
              Copy, then paste directly onto a Figma canvas.
            </li>
            <li data-id="element-9653" data-mp="2daaz68majbvz">
              <strong className="font-semibold text-[#2B211A]" data-id="element-9654" data-mp="2daknzsvlnd03">3.</strong>{' '}
              Install Inter in Figma so the type matches — headings are Inter
              Display (the same family at optical size 32) set at weight 560,
              not bold. Everything arrives as live text layers.
            </li>
          </ol>

          <div className="mt-6 flex flex-wrap items-center gap-3" data-id="element-9655" data-mp="2w7vp7j0skgi6">
            <button type="button" onClick={() => saveAllPng(SCREENS.map(s => s.id))} disabled={busy !== null} className="inline-flex items-center gap-2 rounded-lg bg-[#EA580C] px-4 py-2.5 text-[13px] font-bold text-white transition-colors hover:bg-[#C2410C] disabled:opacity-60" data-id="element-9656" data-mp="2w59fpducbqxb">
              <Download size={14} data-id="element-9657" data-mp="3jx6i6ebwh6lr" />
              {busy ? 'Exporting…' : `Download all ${SCREENS.length} as PNG`}
            </button>

            {/* The vertical plates live on their own page. Someone cutting a
             short has a single job, and making them scroll past twenty
             landscape frames to reach six plates was the wrong order. */}
            <Link to="/app-screen-mockups/creator-pack" className="inline-flex items-center gap-2 rounded-lg border border-[#D8CFC2] bg-white px-3.5 py-2.5 text-[13px] font-bold text-[#2B211A] transition-colors hover:border-[#2B211A]" data-id="element-9658" data-mp="r1m3isy0nfz">
              <Smartphone size={14} data-id="element-9659" data-mp="9jdc53k5a8vf" />
              Creator pack · 9:16 for video
            </Link>

            {/* Frames that can re-compose switch to a phone-width artboard
             with type at full size; the rest stay landscape and say so,
             because squashing a screen whose breakpoints watch the viewport
             would only mislead. */}
            <div role="group" aria-label="Set every artboard orientation" className="inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-9660" data-mp="2l9xt8ch425d0">
              {([[false, 'All landscape', Monitor], [true, 'All mobile', Smartphone]] as const).map(([value, label, Icon]) => <button key={label} type="button" onClick={() => setAll(value)} aria-pressed={pageDefault === value} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12.5px] font-bold transition-colors ${pageDefault === value ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9661" data-mp="12mfpz2xuh0yx">
                  <Icon size={13} data-id="element-9662" data-mp="1qca5xtdgn5ne" />
                  {label}
                </button>)}
            </div>
          </div>

          <p className="mt-4 max-w-[64ch] text-[12.5px] leading-relaxed text-neutral-500" data-id="element-9663" data-mp="1mbq6gj5ezpy3">
            Brand logos are downloaded and embedded into the file, so they
            survive the paste — that is what the short pause after pressing Copy
            is. Drop shadows are dropped rather than faked. The animated screens
            are held on their finished state here — the full Slack thread, the
            complete ChatGPT transcript, every observation landed — so the file
            is never captured mid-animation.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9664" data-mp="1tl0695ay1880">
        <SurfacePills heading="Surface lockups" title="Platform mark and name" note="Transparent artboard — the capsule is translucent, so whatever sits behind it still shows through. PNG is rasterised at 4× with the real type embedded; SVG keeps the word as live text." items={SURFACE_PILLS} data-id="element-9665" data-mp="1ft279nay4spk" />
        <SurfacePills heading="Tool lockups" title="Connected tool mark and name" note="The same lockup for every tool named across the site. Marks come from the shared brand registry, so they match the connections map exactly." items={TOOL_PILLS} data-id="element-9666" data-mp="28fwrqk7rjs31" />
        <SurfacePills heading="CRM lockups" title="CRM mark and name" note="The CRMs a buyer is most likely to already run. Useful for integration pages and campaign creative that names the CRM the reader uses." items={CRM_PILLS} data-id="element-9667" data-mp="23hlhi8dk3fka" />
      </div>

      <div className="mx-auto max-w-[1360px] space-y-14 px-6 pt-14" data-id="element-9668" data-mp="s14w7yfbm4pi">
        {SCREENS.map((s, i) => {
        const copiedNow = copied === s.id;
        const busyNow = busy === s.id;
        const newSection = i === 0 || SCREENS[i - 1].section !== s.section;
        // Portrait applies only where the frame can genuinely re-compose.
        const asPortrait = isPortrait(s);
        return <section key={s.id} data-id="element-9669" data-mp="omd7s7owupct" data-mpi={i}>
              {/* Grouped by the pillar the screen belongs to, so the page
               matches the order you scroll past on the home page. */}
              {newSection && <div className="mb-5 border-b border-[#E7E0D4] pb-3" data-id="element-9670" data-mp="3t1mnn8zgmf2z">
                  <p className="text-[11.5px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-9671" data-mp="amu71zyfigaj">
                    {s.section}
                  </p>
                  {/* What this group of frames is for. One line, so you can
                   skip a whole section without reading its captions. */}
                  {SECTION_BLURBS[s.section] && <p className="mt-1.5 max-w-[76ch] text-[13.5px] leading-relaxed text-[#6B5D52]" data-id="element-9672" data-mp="399q4a6fy1yrx">
                      {SECTION_BLURBS[s.section]}
                    </p>}
                </div>}
              {/* Top-aligned now the caption has made the title block taller —
               end-aligned buttons would drift down beside two lines of copy. */}
              <div className="mb-4 flex flex-wrap items-start justify-between gap-4" data-id="element-9673" data-mp="1rwiu0mscj7xl">
                <div data-id="element-9674" data-mp="uabtbnz5xzqi">
                  <h2 className="font-title flex flex-wrap items-center gap-2 text-[22px] font-semibold tracking-tight text-[#2B211A]" data-id="element-9675" data-mp="ub3m6r1dljd3">
                    {s.name}
                    {/* Frames that cannot re-compose say so, rather than
                     silently ignoring the switch. */}
                    {!s.portrait && <span className="rounded-full bg-[#F1EAE0] px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em] text-[#8A7B6D]" data-id="element-9676" data-mp="18b4lw35wmkqz">
                        Landscape only
                      </span>}
                  </h2>
                  <p className="mt-0.5 text-[12.5px] text-neutral-500" data-id="element-9677" data-mp="2jqkuey7e06eh">
                    {s.note} · giga-{s.id}.svg
                  </p>
                  {/* When to reach for this frame. Sits under the name rather
                   than beside the buttons, so it is read while deciding —
                   not after you have already pressed Download. */}
                  <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-[#6B5D52]" data-id="element-9678" data-mp="3qeuoypvn9ptj">
                    {s.use}
                  </p>
                </div>
                <div className="relative z-10 flex flex-wrap items-center gap-2" onPointerEnter={() => warm(s.id)} onFocus={() => warm(s.id)} data-id="element-9679" data-mp="3ggx2a6tfo131">
                  {/* This frame's own orientation. Sits with its export
                   buttons because it changes what those buttons produce. */}
                  {s.portrait && <div role="group" aria-label={`${s.name} orientation`} className="mr-1 inline-flex items-center rounded-lg border border-[#D8CFC2] bg-white p-1" data-id="element-9680" data-mp="1an6tkywa45cu">
                      {([[false, 'Desktop', Monitor], [true, 'Mobile', Smartphone]] as const).map(([value, label, Icon]) => <button key={label} type="button" onClick={() => setOrientation(o => ({
                  ...o,
                  [s.id]: value
                }))} aria-pressed={asPortrait === value} title={`${label} artboard`} className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[12px] font-bold transition-colors ${asPortrait === value ? 'bg-[#2B211A] text-white' : 'text-[#6B5D52] hover:text-[#2B211A]'}`} data-id="element-9681" data-mp="jimcnn68c1x3">
                          <Icon size={12} data-id="element-9682" data-mp="3w12edk2q802d" />
                          {label}
                        </button>)}
                    </div>}
                  <button type="button" onClick={() => savePng(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white transition-colors hover:bg-black disabled:opacity-60" data-id="element-9683" data-mp="3i2m4jk9khtg0">
                    <Download size={13} data-id="element-9684" data-mp="1l76hj5yigzyu" />
                    {busyNow ? 'Preparing…' : 'Download PNG'}
                  </button>
                  <button type="button" onClick={() => copy(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9685" data-mp="35o6017luxydn">
                    {copiedNow ? <Check size={13} className="text-emerald-600" data-id="element-9686" data-mp="3anaa00glw051" /> : <Copy size={13} data-id="element-9687" data-mp="3ofysn0x0lhku" />}
                    {copiedNow ? 'Copied' : 'Copy for Figma'}
                  </button>
                  <button type="button" onClick={() => save(s.id)} disabled={busyNow} className="inline-flex items-center gap-1.5 rounded-lg border border-[#D8CFC2] bg-white px-3 py-2 text-[12.5px] font-semibold text-[#2B211A] transition-colors hover:border-[#2B211A] disabled:opacity-60" data-id="element-9688" data-mp="3jh5ct1u99ehi">
                    <Download size={13} data-id="element-9689" data-mp="241ptlr8ph74s" />
                    SVG
                  </button>
                </div>
              </div>

              {/* The captured node. Padding sits inside it so the exported
               artboard carries its own margin around the frame.
                `relative isolate` is load-bearing: these screens come from the
               home page, where they sit inside positioned sections. Rendered
               bare here, any decorative `absolute inset-0` layer inside them
               would resolve against the initial containing block — painting a
               viewport-sized panel over the top of the page and swallowing
               clicks on the export buttons. Positioning each frame keeps every
               overlay inside the screen it belongs to. */}
              <div ref={register(s.id)} style={{
            maxWidth: asPortrait ? PORTRAIT_WIDTH + 56 : s.maxWidth
          }} className={`relative isolate overflow-x-auto rounded-2xl ${asPortrait ? 'mx-auto p-7' : `p-10 ${s.wide ? '' : 'mx-auto max-w-[900px]'}`}`} data-id="element-9690" data-mp="3135fonat9lzr">
                {/* Everything inside a frame renders at rest. Frames are
                 usually off-screen, so any `whileInView` entrance would never
                 have fired and would export as its `initial` state — a blank
                 card, or a strike-through that was never drawn. */}
                <StillProvider data-id="element-9691" data-mp="2mwv5029thv3p">
                  {s.chrome ? <AppChrome active={s.active} sidebar={!s.noSidebar} data-id="element-9692" data-mp="2mz7v52pqbbru">
                      {s.selfPadded ? s.render(asPortrait) : <div className="px-5 py-5 md:px-7 md:py-6" data-id="element-9693" data-mp="2r0ja8eaz8els">
                          {s.render(asPortrait)}
                        </div>}
                    </AppChrome> : s.render(asPortrait)}
                </StillProvider>
              </div>
            </section>;
      })}
      </div>
    </main>;
}